import React from 'react';
import PolicyPageLayout, {
  PolicySection, Para, BulletList, BulletItem, SubHead, InfoBox, WarnBox, ContactCard,
} from '../components/policy/PolicyPageLayout';

const toc = [
  { id: 'definitions',          label: 'Definitions' },
  { id: 'subject-matter',       label: 'Subject Matter & Duration' },
  { id: 'processing',           label: 'Nature of Processing' },
  { id: 'controller-obligations', label: 'Controller Obligations' },
  { id: 'processor-obligations',  label: 'Processor Obligations' },
  { id: 'security',             label: 'Security Measures' },
  { id: 'sub-processors',       label: 'Sub-Processors' },
  { id: 'transfers',            label: 'International Transfers' },
  { id: 'retention',            label: 'Data Retention & Deletion' },
  { id: 'data-rights',          label: 'Data Subject Rights' },
  { id: 'dpdp',                 label: 'DPDP Act 2023 Compliance' },
  { id: 'breach',               label: 'Breach Notification' },
  { id: 'audits',               label: 'Audits & Assessments' },
  { id: 'governing-law',        label: 'Governing Law' },
];

const related = [
  { label: 'Privacy Policy',         href: '/privacy-policy' },
  { label: 'Terms of Service',       href: '/terms-of-service' },
  { label: 'Acceptable Use Policy',  href: '/acceptable-use-policy' },
];

const badges = [
  { label: 'GDPR Compliant',  bg: '#dbeafe', color: '#1d4ed8' },
  { label: 'DPDP Act 2023',   bg: '#dcfce7', color: '#15803d' },
  { label: 'IT Act 2000',     bg: '#f3e8ff', color: '#7c3aed' },
];

const DataProcessingAgreementPage: React.FC = () => (
  <PolicyPageLayout
    title="Data Processing Agreement"
    lastUpdated="April 26, 2026"
    effectiveDate="April 26, 2026"
    badges={badges}
    toc={toc}
    related={related}
  >
    <InfoBox>
      This Data Processing Agreement ("DPA") forms part of the agreement between CruvzMail and
      the Customer and applies to all processing of personal data by CruvzMail on behalf of the
      Customer in connection with the Services. By using CruvzMail, the Customer agrees to the
      terms of this DPA.
    </InfoBox>

    <PolicySection id="definitions" number="1" title="Definitions">
      <Para>For the purposes of this DPA, the following definitions apply:</Para>
      <BulletList>
        <BulletItem>
          <strong>"Personal Data"</strong> — Any information relating to an identified or identifiable
          natural person ("data subject"), as defined under GDPR Article 4(1) and DPDP Act 2023.
        </BulletItem>
        <BulletItem>
          <strong>"Processing"</strong> — Any operation performed on Personal Data, including
          collection, storage, use, disclosure, erasure, or destruction.
        </BulletItem>
        <BulletItem>
          <strong>"Controller"</strong> — The Customer: the entity that determines the purposes and
          means of processing Personal Data.
        </BulletItem>
        <BulletItem>
          <strong>"Processor"</strong> — CruvzMail (Cruvz Online Shopping India Pvt Ltd): the entity
          that processes Personal Data on behalf of the Controller.
        </BulletItem>
        <BulletItem>
          <strong>"Data Fiduciary"</strong> — As used in the DPDP Act 2023, equivalent to the
          Controller under GDPR.
        </BulletItem>
        <BulletItem>
          <strong>"Data Principal"</strong> — As used in the DPDP Act 2023, the natural person to
          whom Personal Data relates (equivalent to data subject under GDPR).
        </BulletItem>
        <BulletItem>
          <strong>"Sub-Processor"</strong> — Any third-party processor engaged by CruvzMail to assist
          in processing Personal Data.
        </BulletItem>
        <BulletItem>
          <strong>"GDPR"</strong> — EU General Data Protection Regulation 2016/679.
        </BulletItem>
        <BulletItem>
          <strong>"DPDP Act"</strong> — Digital Personal Data Protection Act 2023 (India).
        </BulletItem>
      </BulletList>
    </PolicySection>

    <PolicySection id="subject-matter" number="2" title="Subject Matter and Duration">
      <Para>
        This DPA governs the processing of Personal Data by CruvzMail as a Data Processor on behalf
        of the Customer (Controller) in connection with the provision of CruvzMail's email hosting,
        webmail, file storage, and associated services.
      </Para>
      <Para>
        This DPA remains in effect for the duration of the Customer's subscription to the Service and
        continues until all Personal Data has been deleted or returned as described in Section 9.
      </Para>
    </PolicySection>

    <PolicySection id="processing" number="3" title="Nature of Processing">
      <SubHead>Categories of Personal Data Processed</SubHead>
      <BulletList>
        <BulletItem>Email addresses, display names, and account credentials of end users</BulletItem>
        <BulletItem>Email content (message bodies, subjects, headers) and attachments</BulletItem>
        <BulletItem>Contact lists, calendar events, tasks, and notes</BulletItem>
        <BulletItem>IP addresses, browser/device identifiers, and session logs for security purposes</BulletItem>
        <BulletItem>Files and documents uploaded to DriveX storage</BulletItem>
        <BulletItem>Billing contact information (name, email, billing address)</BulletItem>
      </BulletList>
      <SubHead>Special Categories</SubHead>
      <Para>
        CruvzMail does not intentionally process special categories of Personal Data (GDPR Article 9)
        such as health data, religious beliefs, or biometric data. The Customer is responsible for
        ensuring that end users do not store such data unless the Customer has appropriate legal basis.
      </Para>
      <SubHead>Purposes of Processing</SubHead>
      <BulletList>
        <BulletItem>Providing email hosting, sending, and receiving services</BulletItem>
        <BulletItem>Spam filtering, malware scanning, and abuse prevention</BulletItem>
        <BulletItem>Account management, authentication, and security</BulletItem>
        <BulletItem>Service monitoring, backup, and disaster recovery</BulletItem>
        <BulletItem>Legal compliance and audit obligations</BulletItem>
      </BulletList>
    </PolicySection>

    <PolicySection id="controller-obligations" number="4" title="Controller Obligations">
      <Para>The Customer, as Controller/Data Fiduciary, agrees to:</Para>
      <BulletList>
        <BulletItem>
          Ensure a valid legal basis exists for all Personal Data provided to CruvzMail for processing
        </BulletItem>
        <BulletItem>
          Provide CruvzMail with clear and lawful instructions regarding the processing of Personal Data
        </BulletItem>
        <BulletItem>
          Ensure that end users whose data is processed are informed of the use of CruvzMail as a
          processor through appropriate privacy notices
        </BulletItem>
        <BulletItem>
          Comply with all applicable data protection laws including DPDP Act 2023 and GDPR where
          applicable
        </BulletItem>
        <BulletItem>
          Not instruct CruvzMail to process Personal Data in a manner that would violate applicable law
        </BulletItem>
      </BulletList>
    </PolicySection>

    <PolicySection id="processor-obligations" number="5" title="Processor Obligations">
      <Para>CruvzMail, as Data Processor/Data Fiduciary, agrees to:</Para>
      <BulletList>
        <BulletItem>
          Process Personal Data only on documented instructions from the Controller, except where
          required by applicable law
        </BulletItem>
        <BulletItem>
          Ensure that all personnel authorized to process Personal Data are bound by appropriate
          confidentiality obligations
        </BulletItem>
        <BulletItem>
          Implement and maintain appropriate technical and organizational security measures (see
          Section 6)
        </BulletItem>
        <BulletItem>
          Not engage new Sub-Processors without either prior specific authorization or general
          authorization with advance notice (see Section 7)
        </BulletItem>
        <BulletItem>
          Assist the Controller in responding to Data Subject rights requests within applicable
          statutory timeframes
        </BulletItem>
        <BulletItem>
          Notify the Controller of any Personal Data breach within 72 hours of becoming aware of it
          (see Section 12)
        </BulletItem>
        <BulletItem>
          Delete or return all Personal Data at the end of the service relationship (see Section 9)
        </BulletItem>
      </BulletList>
    </PolicySection>

    <PolicySection id="security" number="6" title="Security Measures">
      <Para>
        CruvzMail implements appropriate technical and organizational measures to protect Personal Data
        against unauthorized access, loss, alteration, or disclosure. These measures include:
      </Para>
      <SubHead>Technical Measures</SubHead>
      <BulletList>
        <BulletItem>TLS 1.2+ encryption for all data in transit (email, webmail, API)</BulletItem>
        <BulletItem>AES-256 encryption for data at rest on all storage volumes</BulletItem>
        <BulletItem>JWT-based authentication with short-lived access tokens (15 minutes)</BulletItem>
        <BulletItem>Multi-factor authentication (TOTP) available for all accounts</BulletItem>
        <BulletItem>Automated malware scanning (ClamAV) on all uploaded files</BulletItem>
        <BulletItem>Web Application Firewall (WAF) and DDoS protection at load balancer level</BulletItem>
        <BulletItem>Automated daily encrypted backups with 30-day retention</BulletItem>
        <BulletItem>Network segmentation — databases only accessible from application VMs</BulletItem>
      </BulletList>
      <SubHead>Organizational Measures</SubHead>
      <BulletList>
        <BulletItem>Role-based access control — minimal privilege principle enforced</BulletItem>
        <BulletItem>Security activity logging with 180-day retention (CERT-In compliant)</BulletItem>
        <BulletItem>Formal incident response plan with defined escalation procedures</BulletItem>
        <BulletItem>Regular security assessments and vulnerability scanning</BulletItem>
        <BulletItem>Personnel security training on data protection obligations</BulletItem>
      </BulletList>
    </PolicySection>

    <PolicySection id="sub-processors" number="7" title="Sub-Processors">
      <Para>
        CruvzMail uses the following Sub-Processors in the course of providing the Service:
      </Para>
      <BulletList>
        <BulletItem>
          <strong>Razorpay Financial Solutions Pvt Ltd (India)</strong> — Payment processing for
          subscription billing. Processes billing contact name, email, and payment card data.
        </BulletItem>
        <BulletItem>
          <strong>Let's Encrypt / ISRG (USA)</strong> — TLS certificate issuance. No personal data
          is shared beyond domain names during certificate issuance.
        </BulletItem>
      </BulletList>
      <Para>
        All Sub-Processors are bound by data protection agreements consistent with this DPA. We will
        notify the Customer of any intended addition of new Sub-Processors with at least 14 days
        advance notice, during which the Customer may object on reasonable grounds.
      </Para>
    </PolicySection>

    <PolicySection id="transfers" number="8" title="International Data Transfers">
      <Para>
        CruvzMail stores and processes all email data within India on infrastructure hosted in
        Proxmox VE clusters operated by Cruvz Online Shopping India Pvt Ltd. Payment processing
        data is handled by Razorpay within India.
      </Para>
      <Para>
        Where any international transfer is required (e.g., for TLS certificate issuance), CruvzMail
        will ensure appropriate transfer mechanisms are in place, including standard contractual
        clauses where required by GDPR or DPDP Act provisions.
      </Para>
    </PolicySection>

    <PolicySection id="retention" number="9" title="Data Retention and Deletion">
      <Para>
        CruvzMail retains Personal Data for the duration of the Customer's active subscription.
        Upon account closure or subscription termination:
      </Para>
      <BulletList>
        <BulletItem>
          Account data (emails, files, contacts, calendar) is accessible for 30 days for export
        </BulletItem>
        <BulletItem>
          All account data is permanently deleted 30 days after account deactivation
        </BulletItem>
        <BulletItem>
          Security and audit logs are retained for 180 days as required by CERT-In guidelines,
          then permanently deleted
        </BulletItem>
        <BulletItem>
          Backup copies containing Personal Data are purged within 90 days of account deletion
        </BulletItem>
      </BulletList>
      <Para>
        The Customer may request confirmation of deletion. CruvzMail will provide written confirmation
        within 30 days of completing the deletion process.
      </Para>
    </PolicySection>

    <PolicySection id="data-rights" number="10" title="Data Subject Rights">
      <Para>
        CruvzMail will assist the Controller in fulfilling Data Subject rights requests within
        applicable statutory timeframes. Supported rights include:
      </Para>
      <BulletList>
        <BulletItem><strong>Right of Access (GDPR Art. 15 / DPDP Sec. 11):</strong> Users may access and download their data via webmail account settings</BulletItem>
        <BulletItem><strong>Right to Rectification (GDPR Art. 16 / DPDP Sec. 12):</strong> Users may update their personal information directly</BulletItem>
        <BulletItem><strong>Right to Erasure (GDPR Art. 17 / DPDP Sec. 13):</strong> Account deletion is available from account settings or via administrator</BulletItem>
        <BulletItem><strong>Right to Data Portability (GDPR Art. 20 / DPDP Sec. 16):</strong> Email data can be exported in standard formats (EML, CSV for contacts)</BulletItem>
        <BulletItem><strong>Right to Object (GDPR Art. 21):</strong> Customers may object to data processing by contacting privacy@cruvzmail.com</BulletItem>
      </BulletList>
    </PolicySection>

    <PolicySection id="dpdp" number="11" title="DPDP Act 2023 Specific Provisions">
      <InfoBox>
        CruvzMail operates in compliance with India's Digital Personal Data Protection Act 2023
        (DPDP Act), which came into effect for applicable entities as notified by the Central Government.
      </InfoBox>
      <Para>In accordance with the DPDP Act 2023, CruvzMail as Data Fiduciary:</Para>
      <BulletList>
        <BulletItem>
          Processes Digital Personal Data only for lawful purposes with appropriate consent or
          legitimate use as defined under the Act
        </BulletItem>
        <BulletItem>
          Maintains a Data Protection Officer (DPO) contact for compliance purposes: dpo@cruvzmail.com
        </BulletItem>
        <BulletItem>
          Implements organizational measures to ensure accuracy and security of Personal Data
        </BulletItem>
        <BulletItem>
          Notifies the Data Protection Board of India of significant data breaches as required
        </BulletItem>
        <BulletItem>
          Erases Personal Data when the purpose of processing has been fulfilled or consent withdrawn
        </BulletItem>
        <BulletItem>
          Does not transfer Personal Data outside India except in compliance with notified provisions
          of the DPDP Act regarding cross-border transfers
        </BulletItem>
      </BulletList>
    </PolicySection>

    <PolicySection id="breach" number="12" title="Data Breach Notification">
      <WarnBox>
        In the event of a Personal Data breach, CruvzMail will notify the Controller within 72 hours
        of becoming aware of the breach, regardless of the day or time.
      </WarnBox>
      <Para>The breach notification will include, to the extent known:</Para>
      <BulletList>
        <BulletItem>Nature of the breach and categories of Personal Data affected</BulletItem>
        <BulletItem>Approximate number of data subjects and records affected</BulletItem>
        <BulletItem>Likely consequences of the breach</BulletItem>
        <BulletItem>Measures taken or proposed to address the breach and mitigate its effects</BulletItem>
        <BulletItem>Contact details of the Data Protection Officer or designated point of contact</BulletItem>
      </BulletList>
      <Para>
        The Controller is responsible for notifying relevant supervisory authorities (including India's
        Data Protection Board) and affected data subjects in accordance with applicable law.
      </Para>
    </PolicySection>

    <PolicySection id="audits" number="13" title="Audits and Assessments">
      <Para>
        CruvzMail will make available to the Controller all information reasonably necessary to
        demonstrate compliance with this DPA. The Customer may, upon reasonable notice (minimum
        30 days) and at their own cost, audit or inspect CruvzMail's data processing activities
        relating to Personal Data, or commission a qualified independent third-party auditor to do so.
      </Para>
      <Para>
        Audits shall be conducted during normal business hours and in a manner that minimizes
        disruption to CruvzMail's operations. Results of audits are confidential.
      </Para>
    </PolicySection>

    <PolicySection id="governing-law" number="14" title="Governing Law">
      <Para>
        This DPA shall be governed by the laws of India. Disputes arising from this DPA shall be
        subject to the exclusive jurisdiction of the courts in India. Where the Customer is subject to
        GDPR, this DPA shall be interpreted in a manner consistent with GDPR requirements, and the
        standard contractual clauses (SCCs) may be incorporated by reference where applicable.
      </Para>
      <ContactCard>
        <strong>Data Protection Officer:</strong> dpo@cruvzmail.com<br />
        <strong>Privacy inquiries:</strong> privacy@cruvzmail.com<br />
        <strong>Legal:</strong> legal@cruvzmail.com<br />
        <br />
        <strong>CruvzMail</strong> — Cruvz Online Shopping India Pvt Ltd<br />
        cruvzmail.com
      </ContactCard>
    </PolicySection>

  </PolicyPageLayout>
);

export default DataProcessingAgreementPage;
